import {
  disableAutoPublishHeader,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { getPushEndpointListUrl } from "src/queries/playPushEndpoints"
import type { OrgParams, PoolParams, TargetParams } from "src/queries/types"
import { getPoolUrl } from "./playPools"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /targets/?org=${orgUuid} */
export const getOrgTargetListUrl = ({ orgUuid }: OrgParams) => `/targets/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/targets/${targetUuid}/?org=${orgUuid} */
export const getTargetUrl = ({ orgUuid, poolUuid, targetUuid }: Required<TargetParams>) =>
  `/pools/${poolUuid}/targets/${targetUuid}/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/targets/?org=${orgUuid} */
export const getTargetListUrl = ({ orgUuid, poolUuid }: Required<PoolParams>) =>
  `/pools/${poolUuid}/targets/?org=${orgUuid}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useOrgTargetList = (params?: OrgParams) => {
  const mergedParams = useRouteParams(params)
  const orgListUrl = getOrgTargetListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedTargetList>("ott", orgListUrl)
}

export const useTarget = (params?: TargetParams) => {
  const mergedParams = useRouteParams(params)
  const url = getTargetUrl(mergedParams)

  return useGetQuery<api.ott.Target>("ott", url)
}

export const useTargetList = (params?: TargetParams) => {
  const mergedParams = useRouteParams(params)
  const url = getTargetListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedTargetList>("ott", url)
}

export const useAddTarget = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getTargetListUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.Target>
  type TResponse = api.ott.Target

  const headers = disableAutoPublishHeader
  return usePostQuery<TRequest, TResponse>("ott", listUrl, [poolUrl], { headers })
}

export const useUpdateTarget = (params?: TargetParams) => {
  const mergedParams = useRouteParams(params)
  const url = getTargetUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.Target>
  type TResponse = api.ott.Target

  return usePatchQuery<TRequest, TResponse>("ott", url, [poolUrl])
}

export const useDeleteTarget = (params?: TargetParams) => {
  const mergedParams = useRouteParams(params)
  const url = getTargetUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)
  const pushEndpointListUrl = getPushEndpointListUrl(mergedParams)

  return useDeleteQuery("ott", url, [poolUrl, pushEndpointListUrl])
}
